﻿import { Style } from 'ol/style';

import {
  IMAGE_LOADING,
  IMAGE_LOADED,
  IMAGE_ERROR,
  DEFAULT_MARK_SIZE,
  DEFAULT_EXTERNALGRAPHIC_SIZE,
  DEFAULT_MARK_WELLKNOWNNAME
} from '../constants';
import { memoizeStyleFunction, memoizeSymbolizerInfoFunction, hexToRGB, cloneStyleValue, convertSize } from './styleUtils';
import {
  imageLoadingPointStyle,
  imageErrorPointStyle,
  emptyStyle,
} from './static';
import { createCachedImageStyle, getCachedImage, getImageLoadingState } from '../imageCache';
import getWellKnownSymbol from './wellknown';
import evaluate, { expressionOrDefault } from '../olEvaluator';
import { getSimpleFill, getSimpleStroke } from './simpleStyles';

const defaultMarkFill = getSimpleFill({ styling: { fill: '#888888' } });
const defaultMarkStroke = getSimpleStroke({ styling: { stroke: {} } });

/**
 * @private
 * @param  {PointSymbolizer} pointsymbolizer [description]
 * @return {object} openlayers style
 */
function pointStyle(pointsymbolizer) {
  const { graphic: style } = pointsymbolizer;

  // If the point size is a dynamic expression, use the default point size and update in-place later.
  let pointSizeValue = expressionOrDefault(style.size, DEFAULT_MARK_SIZE);

  // If the point rotation is a dynamic expression, use 0 as default rotation and update in-place later.
  const rotationDegrees = expressionOrDefault(style.rotation, 0.0);
  const displacementX = resolveStaticNumber(
    style.displacement && style.displacement.displacementx,
    0
  );
  const displacementY = resolveStaticNumber(
    style.displacement && style.displacement.displacementy,
    0
  );

  const opacityRaw = style.opacity;
  var opacity =
    opacityRaw && (opacityRaw.type === 'propertyname' || opacityRaw.prop)
      ? 1
      : (opacityRaw === null || opacityRaw === '') ? 1 : Number(opacityRaw);

  const displacement = style.displacement == null ? [0, 0] : [displacementX, displacementY];

  const anchorX = resolveStaticNumber(
    style.anchorpoint && style.anchorpoint.anchorpointx,
    0
  );
  const anchorY = resolveStaticNumber(
    style.anchorpoint && style.anchorpoint.anchorpointy,
    0
  );

  const anchor = style.anchorpoint == null ? [0.5, 0.5] : [anchorX, 1 - anchorY];

  if (style.externalgraphic && style.externalgraphic.onlineresource) {
    // For external graphics without explicit size, use the default external graphic size.
    if (!style.size) {
      pointSizeValue = DEFAULT_EXTERNALGRAPHIC_SIZE;
    }

    const imageUrl = style.externalgraphic.onlineresource;

    // Use fallback point styles when image hasn't been loaded yet.
    switch (getImageLoadingState(imageUrl)) {
      case IMAGE_LOADED:
        if (!getCachedImage(imageUrl)) {
          return imageLoadingPointStyle;
        }
        return createCachedImageStyle(
          imageUrl,
          pointSizeValue,
          rotationDegrees,
          opacity,
          displacement,
          anchor
        );
      case IMAGE_LOADING:
        return imageLoadingPointStyle;
      case IMAGE_ERROR:
        return imageErrorPointStyle;
      default:
        // A symbolizer should have loading state metadata, but return IMAGE_LOADING just in case.
        return imageLoadingPointStyle;
    }
  }

  if (style.mark) {
    const { wellknownname } = style.mark;
    const olFill = getSimpleFill(style.mark.fill);
    const olStroke = getSimpleStroke(style.mark.stroke);

    return new Style({
      // Note: size will be set dynamically later.
      image: getWellKnownSymbol(
        wellknownname,
        pointSizeValue,
        olStroke,
        olFill,
        rotationDegrees,
        displacement
      ),
    });
  }

  // SLD spec: when no ExternalGraphic or Mark is specified,
  // use a square of 6 pixels with 50% gray fill and a black outline.
  return new Style({
    image: getWellKnownSymbol(
      DEFAULT_MARK_WELLKNOWNNAME,
      pointSizeValue,
      defaultMarkStroke,
      defaultMarkFill,
      rotationDegrees,
      displacement
    ),
  });
}

const cachedPointStyle = memoizeStyleFunction(pointStyle);
const cachedSymbolizerInfo = memoizeSymbolizerInfoFunction();

/**
 * @private
 * Get an OL point style instance for a feature according to a symbolizer.
 * @param {object} symbolizer SLD symbolizer object.
 * @param {ol/Feature} feature OpenLayers Feature.
 * @param {Function} getProperty A property getter: (feature, propertyName) => property value.
 * @returns {ol/Style} OpenLayers style instance.
 */
function getPointStyle(symbolizer, feature, getProperty) {
    if (feature.get('features') && feature.get('features').length == 1) {
        feature = feature.get('features')[0];
    }

    // According to SLD spec, when a point symbolizer has no Graphic, nothing will be rendered.
    if (!(symbolizer && symbolizer.graphic)) {
        return emptyStyle;
    }

    let olStyle = cloneStyleValue(cachedPointStyle(symbolizer));
    cachedSymbolizerInfo(symbolizer);

    const { graphic } = symbolizer;
    const { size } = graphic;
    const eg = graphic.externalgraphic;
    const displacement = graphic.displacement;
    const defaultSize = graphic.externalgraphic
        ? DEFAULT_EXTERNALGRAPHIC_SIZE
        : DEFAULT_MARK_SIZE;

    let iconDisplacementx = 0;
    let iconDisplacementy = 0;

    if (displacement) {
        const displacementx = displacement.displacementx;
        const displacementy = displacement.displacementy;

        if (displacementx) {
            const displacementXValue = resolveValue(displacementx, feature);
            const numericDisplacementX = Number(displacementXValue);
            if (Number.isNaN(numericDisplacementX)) {
                iconDisplacementx = 0;
            } else {
                iconDisplacementx = numericDisplacementX;
            }
        }

        if (displacementy) {
            const displacementYValue = resolveValue(displacementy, feature);
            const numericDisplacementY = Number(displacementYValue);
            if (Number.isNaN(numericDisplacementY)) {
                iconDisplacementy = 0;
            } else {
                iconDisplacementy = numericDisplacementY;
            }
        }
    }

    const convertedDisplacementX = convertSymbolizerSize(
        iconDisplacementx,
        feature,
        symbolizer
    );
    iconDisplacementx = Number.isNaN(convertedDisplacementX)
        ? iconDisplacementx
        : convertedDisplacementX;

    const convertedDisplacementY = convertSymbolizerSize(
        iconDisplacementy,
        feature,
        symbolizer
    );
    iconDisplacementy = Number.isNaN(convertedDisplacementY)
        ? iconDisplacementy
        : convertedDisplacementY;

    if (eg && eg.onlineresource && (typeof eg.onlineresource != 'string')) {
        const imageUrl = resolveOnlineResource(eg, feature, getProperty);

        // If blank -> render nothing (or your error style)
        if (!imageUrl) return emptyStyle;

        // If size is not defined for an external graphic, use default external graphic size.
        const pointSizeValue = graphic.size
            ? expressionOrDefault(graphic.size, DEFAULT_EXTERNALGRAPHIC_SIZE)
            : DEFAULT_EXTERNALGRAPHIC_SIZE;

        // Use fallback point styles when image hasn't been loaded yet.
        switch (getImageLoadingState(imageUrl)) {
            case IMAGE_LOADED: {
                if (!getCachedImage(imageUrl)) {
                    return imageLoadingPointStyle;
                }
                // Option A (works with your existing caching approach):
                // Get a style for *this url* (still safe to mutate scale/rotation after)
                olStyle = createCachedImageStyle(
                    imageUrl,
                    pointSizeValue,
                    0,          // rotation set later below
                    1,          // opacity set later if you want
                    [0, 0],     // displacement set later if you want
                    [0.5, 0.5]  // anchor set later if you want
                );

                break;
            }
            case IMAGE_LOADING:
                return imageLoadingPointStyle;
            case IMAGE_ERROR:
                return imageErrorPointStyle;
            default:
                return imageLoadingPointStyle;
        }
    }

    if (feature.get('xstate') == null || feature.get('xstate') == 0) {
        olStyle.setZIndex(0);
    } else {
        olStyle.setZIndex(100);
    }

  // Reset previous calculated point geometry left by evaluating point style for a line or polygon feature.
  olStyle.setGeometry(null);

  let olImage = olStyle.getImage();

  // Apply dynamic values to the cached OL style instance before returning it.

  // --- Update size ---
    const pointSize = resolvePointSizeInPixels(
        size,
        feature,
        getProperty,
        defaultSize,
        symbolizer
    );
    if (pointSize != null) {
        if (graphic.externalgraphic && graphic.externalgraphic.onlineresource) {
            const height = olImage.getSize()[1];
            const scale = pointSize / height || 1;
            olImage.setScale(scale);
        } else if (graphic.mark && graphic.mark.wellknownname === 'circle') {
            // Note: only ol/style/Circle has a setter for radius. RegularShape does not.
            olImage.setRadius(pointSize * 0.5);
        } else {
            // For a non-Circle RegularShape, create a new olImage in order to update the size.
            olImage = getWellKnownSymbol(
                (graphic.mark && graphic.mark.wellknownname) || DEFAULT_MARK_WELLKNOWNNAME,
                pointSize,
                // Note: re-use stroke and fill instances for a (small?) performance gain.
                olImage.getStroke(),
                olImage.getFill()
            );
            olStyle.setImage(olImage);
        }
    }

  // --- Update dynamic rotation ---
  const { rotation } = graphic;
    if (rotation && rotation.type === 'expression') {
        const rotationDegrees =
            Number(evaluate(rotation, feature, getProperty)) || 0.0;
        // Note: OL angles are in radians.
        const rotationRadians = (Math.PI * rotationDegrees) / 180.0;
        olImage.setRotation(rotationRadians);
    } else if (rotation && rotation.type === 'propertyname') {
        const rotationDegrees =
            Number(feature.get(rotation.value)) || 0.0;
        // Note: OL angles are in radians.
        const rotationRadians = (Math.PI * rotationDegrees) / 180.0;
        olImage.setRotation(rotationRadians);
    } else if (rotation && rotation.prop) {
        const rotationDegrees =
            Number(feature.get(rotation.prop)) || 0.0;
        const rotationRadians = (Math.PI * rotationDegrees) / 180.0;
        olImage.setRotation(rotationRadians);
    } else if (rotation) {
        const rotationDegrees =
            Number(rotation) || 0.0;
        const rotationRadians = (Math.PI * rotationDegrees) / 180.0;
        olImage.setRotation(rotationRadians);
    }

    if (graphic.opacity && graphic.opacity.type === 'expression') {
        let opacityValue = evaluate(graphic.opacity, feature, getProperty);

        if(opacityValue == null){
            opacityValue = 1.0;
        }

        const numericOpacity = Number(opacityValue);
        if (!Number.isNaN(numericOpacity) && olImage && olImage.setOpacity) {
            olImage.setOpacity(numericOpacity);
        }
    } else if (graphic.opacity && graphic.opacity.type === 'propertyname') {
        let opacityValue = feature.get(graphic.opacity.value);

        if(opacityValue == null){
            opacityValue = 1.0;
        }

        const numericOpacity = Number(opacityValue);
        if (!Number.isNaN(numericOpacity) && olImage && olImage.setOpacity) {
            olImage.setOpacity(numericOpacity);
        }
    } else if (graphic.opacity && graphic.opacity.prop) {
        let opacityValue = feature.get(graphic.opacity.prop);

        if(opacityValue == null){
            opacityValue = 1.0;
        }

        const numericOpacity = Number(opacityValue);
        if (!Number.isNaN(numericOpacity) && olImage && olImage.setOpacity) {
            olImage.setOpacity(numericOpacity);
        }
    }  else if (graphic.opacity) {
        const numericOpacity = Number(graphic.opacity);
        if (!Number.isNaN(numericOpacity) && olImage && olImage.setOpacity) {
            olImage.setOpacity(numericOpacity);
        }
    }

    const fillStyling = symbolizer?.graphic?.mark?.fill?.styling;
    if (fillStyling && (fillStyling.fill || fillStyling.fillOpacity)) {
        const fillValue = resolveValue(fillStyling.fill, feature);
        const fillOpacityValue = resolveValue(fillStyling.fillOpacity, feature);

        if (fillValue != null || fillOpacityValue != null) {
            const fillColor = buildColor(fillValue, fillOpacityValue);

            if (fillColor != null && olImage.getFill()) {
                olImage.getFill().setColor(fillColor);
            } else if ((fillOpacityValue || fillOpacityValue === 0) && olImage.getFill()) {
                const currentColor = olImage.getFill().getColor();
                if (isHexColor(currentColor)) {
                    olImage.getFill().setColor(
                        hexToRGB(currentColor, fillOpacityValue)
                    );
                }
            }
        }

        olStyle.setImage(olImage);
    }

    const strokeStyling = symbolizer?.graphic?.mark?.stroke?.styling;
    if (strokeStyling && (strokeStyling.stroke || strokeStyling.strokeOpacity || strokeStyling.strokeWidth)) {
        const strokeValue = resolveValue(strokeStyling.stroke, feature);
        const strokeOpacityValue = resolveValue(strokeStyling.strokeOpacity, feature);
        const strokeWidthValue = resolveValue(strokeStyling.strokeWidth, feature);
        const defaultStrokeWidth = resolveStaticNumber(strokeStyling.strokeWidth, 1);

        if (strokeValue != null || strokeOpacityValue != null || strokeWidthValue != null) {
            const strokeColor = buildColor(strokeValue, strokeOpacityValue);
            const resolvedWidth = resolveDynamicNumber(strokeWidthValue, defaultStrokeWidth);
            const convertedWidth = convertSymbolizerSize(
                resolvedWidth,
                feature,
                symbolizer
            );
            const finalStrokeWidth = Number.isNaN(convertedWidth)
                ? resolvedWidth
                : convertedWidth;

            let olStroke = olImage.getStroke && olImage.getStroke();
            if (!olStroke && olImage.setStroke) {
                const baseColor =
                    strokeColor != null
                        ? strokeColor
                        : (strokeOpacityValue || strokeOpacityValue === 0)
                            ? hexToRGB('#000000', strokeOpacityValue)
                            : '#000000';
                olStroke = getSimpleStroke({
                    styling: {
                        stroke: baseColor,
                        strokeWidth: finalStrokeWidth != null ? finalStrokeWidth : 1
                    }
                });
                olImage.setStroke(olStroke);
            }

            if (olStroke) {
                if (strokeColor != null) {
                    olStroke.setColor(strokeColor);
                } else if (strokeOpacityValue || strokeOpacityValue === 0) {
                    const currentColor = olStroke.getColor();
                    if (isHexColor(currentColor)) {
                        olStroke.setColor(hexToRGB(currentColor, strokeOpacityValue));
                    }
                }

                if (finalStrokeWidth != null) {
                    olStroke.setWidth(finalStrokeWidth);
                }
                olImage.setStroke(olStroke);
            }

            olStyle.setImage(olImage);
        }
    }

    const dispX_screen = iconDisplacementx;
    const dispY_screen = iconDisplacementy;

    // scale can be number or [sx, sy]
    const s = olImage.getScale?.() ?? 1;
    const sx = Array.isArray(s) ? s[0] : s;
    const sy = Array.isArray(s) ? s[1] : s;

    let new_dispX_screen = dispX_screen / (sx || 1);
    let new_dispY_screen = dispY_screen / (sy || 1);

    olImage.setDisplacement([
        new_dispX_screen,
        new_dispY_screen,
    ]);

    return olStyle;
}

function resolveOnlineResource(externalgraphic, feature, getProperty) {
    const src = externalgraphic?.onlineresource;
    if (!src) return null;

    if (typeof src === 'string') return src;

    if (src.prop) {
        const value = feature.get(src.prop);
        if (value == null || value === '') {
            return rootURL + 'img/sld/unknown_img.png';
        }
        return normalizeOnlineResource(value);
    }

    if (src.type === 'propertyname') {
        let value = feature.get(src.value);

        if (value == null || value === '') {
            return rootURL + 'img/sld/unknown_img.png';
        }

        value = String(value);
        if (value.indexOf('http') == 0) {
            return value;
        }
        if (value.indexOf('/') == 0) {
            return rootURL + value.substring(1);
        }
        return rootURL + value;
    }

    if (src.type === 'expression') {
        return normalizeOnlineResource(evaluate(src, feature, getProperty));
    }

    if (src.value != null) {
        return normalizeOnlineResource(feature.get(src.value));
    }

    // fallback
    return normalizeOnlineResource(src);
}

function normalizeOnlineResource(value) {
    if (value == null) {
        return null;
    }
    const normalized = String(value).trim();
    return normalized === '' ? null : normalized;
}

function convertSymbolizerSize(value, feature, symbolizer) {
    return convertSize(
        Number(value),
        feature.get('currentResolution'),
        feature.get('currentUom'),
        symbolizer && symbolizer.uom
    );
}

function resolvePointSizeInPixels(size, feature, getProperty, defaultSize, symbolizer) {
    if (size == null) {
        return null;
    }

    let numericSize;
    if (size && size.type === 'expression') {
        numericSize = Number(evaluate(size, feature, getProperty));
    } else if (size && size.type === 'propertyname') {
        numericSize = Number(feature.get(size.value));
    } else if (size && size.prop) {
        numericSize = Number(feature.get(size.prop));
    } else {
        numericSize = Number(size);
    }

    if (Number.isNaN(numericSize) || numericSize === 0) {
        numericSize = defaultSize;
    }

    const convertedSize = convertSymbolizerSize(numericSize, feature, symbolizer);
    return Number.isNaN(convertedSize) ? numericSize : convertedSize;
}

function resolveValue(value, feature) {
    if (value && value.prop) {
        return feature.get(value.prop);
    }
    if (value && value.type === 'propertyname') {
        return feature.get(value.value);
    }
    return value;
}

function resolveStaticNumber(value, fallback) {
    if (isDynamicValue(value)) {
        return fallback;
    }
    if (value == null || value === '') {
        return fallback;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? fallback : numericValue;
}

function resolveDynamicNumber(value, fallback) {
    if (value == null || value === '') {
        return fallback;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? fallback : numericValue;
}

function isDynamicValue(value) {
    return (
        value &&
        typeof value === 'object' &&
        (value.prop || value.type === 'propertyname' || value.type === 'expression')
    );
}

function buildColor(value, opacity) {
    if (!isColorLikeValue(value)) {
        return null;
    }
    if ((opacity || opacity === 0) && isHexColor(value)) {
        return hexToRGB(value, opacity);
    }
    return value;
}

function isHexColor(value) {
    return typeof value === 'string' && value.slice(0, 1) === '#';
}

function isColorLikeValue(value) {
    if (typeof value === 'string') {
        return value !== '';
    }
    if (Array.isArray(value)) {
        return value.length >= 3;
    }
    return false;
}

export default getPointStyle;
